import { isHtmlContent } from "./noteContentFormat";
import { htmlNoteTextStable, stripMarkdownSyntax } from "./noteFingerprint";

// The fingerprint projections moved to noteFingerprint.ts (the parity
// contract lives in one file); re-exported here so display-side importers
// keep their import paths.
export { htmlNoteTextStable, stripMarkdownSyntax } from "./noteFingerprint";

/**
 * Note/feedback fields hold MARKDOWN (the stored form) with legacy Lexical
 * HTML still possible during the conversion transition, so every helper here
 * routes on `isHtmlContent`: the markdown branch is cheap string work, the
 * HTML branch keeps the original DOMParser logic.
 *
 * The HTML branch parses with `DOMParser`, which is expensive. These run per
 * rich-text field during render — e.g. the funder track allocation matrix
 * calls `isRichTextBlank` three times per cell (note + positive + negative),
 * across proposals × recommenders — and the same field strings get re-parsed on
 * every re-render. A bounded memo cache keyed by the input string collapses
 * those repeats to one parse per distinct value, which is where the cost went
 * (a recorded prod trace showed ~6,800 parses / 388ms of `parseFromString` on a
 * single matrix view).
 *
 * The cache is purely a performance layer: results are a deterministic function
 * of the input string, so caching is semantics-preserving.
 */
// This file is typechecked by the worker too (via hashNotes), whose tsconfig
// has no DOM lib. DOMParser is only touched behind runtime guards; declare
// the minimal surface used so the browser branches typecheck everywhere.
declare const DOMParser:
  | undefined
  | (new () => {
      parseFromString(
        source: string,
        mimeType: string,
      ): { body: { textContent: string | null } };
    });

const MAX_CACHE_ENTRIES = 2000;

/**
 * Reads `key` from `cache`, computing and storing it on a miss. Bounded with
 * simple FIFO eviction (Map preserves insertion order) so a round with many
 * distinct rich-text fields can't grow the cache without limit.
 */
function memoize<V>(cache: Map<string, V>, key: string, compute: () => V): V {
  const cached = cache.get(key);
  if (cached !== undefined) return cached;
  const value = compute();
  if (cache.size >= MAX_CACHE_ENTRIES) {
    const oldest = cache.keys().next().value;
    if (oldest !== undefined) cache.delete(oldest);
  }
  cache.set(key, value);
  return value;
}

const blankCache = new Map<string, boolean>();
const plainTextCache = new Map<string, string>();
const multilineTextCache = new Map<string, string>();

const REGEX_NBSP = /&nbsp;|&#160;| /g;

/**
 * Decode the entity references Lexical's serializer emits into text, for the
 * SSR/no-DOM branches (a browser `DOMParser` decodes these itself; without
 * this, server-side projections of legacy HTML show literal `&amp;`).
 * `&amp;` decodes last so `&amp;lt;` becomes `&lt;`, not `<`.
 */
function decodeCommonEntities(text: string): string {
  return text
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#0*39;|&#x0*27;/gi, "'")
    .replace(/&amp;/gi, "&");
}

/** Drop trailing spaces per line and collapse blank-line runs to one break. */
function tidyLines(text: string): string {
  return text
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/**
 * Returns true when the given HTML string has no meaningful content.
 *
 * Needed because Lexical normalizes empty rich-text fields to strings like
 * `<p><br></p>` and `<p class="mb-2 last:mb-0"><br></p>`, which `.trim()`
 * alone treats as non-empty. Parses the HTML and checks `textContent`,
 * treating embedded images/videos/iframes as non-blank.
 */
export function isRichTextBlank(value: string | null | undefined): boolean {
  if (!value) return true;
  // Fast path: embedded media is always non-blank, regardless of text. Cheap to
  // detect and lets the common "image-only" field skip the parse entirely.
  // The markdown image form counts as media the same way.
  if (/<(?:img|video|iframe)\b/i.test(value) || /!\[[^\]]*\]\(/.test(value)) {
    return false;
  }
  return memoize(blankCache, value, () => {
    if (!isHtmlContent(value)) {
      // Markdown/plain text: blank means no VISIBLE content — project
      // first, so a marker-only value (`-`, `# `) that renders empty also
      // reads blank, agreeing with the plain-text and fingerprint
      // projections. Inside the memo: this runs per rich-text field per
      // render on the matrix views, and post-conversion nearly every value
      // takes this branch.
      return stripMarkdownSyntax(value).replace(/\s+/g, "").length === 0;
    }
    if (typeof DOMParser === "undefined") {
      // SSR / non-DOM environments: use the shared stable projection — it
      // decodes EVERY entity variant (`&#xA0;`, `&#0160;`, …), so a cleared
      // field can't read non-blank here while hashing as empty.
      return htmlNoteTextStable(value).length === 0;
    }
    const doc = new DOMParser!().parseFromString(value, "text/html");
    const text = (doc.body.textContent ?? "").replace(/ /g, " ").trim();
    // Media was already ruled out by the fast path above, so non-empty text is
    // the only way this can be non-blank here.
    return text.length === 0;
  });
}

/**
 * Extracts a plain-text rendering of a rich-text (HTML) value, collapsing
 * whitespace. Useful for compact previews where rendering the full markup
 * would be unsafe (e.g. nested inside a button) or unwanted. Returns "" for
 * blank/empty input; mirrors `isRichTextBlank`'s DOM/SSR split.
 */
export function richTextToPlainText(value: string | null | undefined): string {
  if (!value) return "";
  return memoize(plainTextCache, value, () => {
    if (!isHtmlContent(value)) {
      return stripMarkdownSyntax(value).replace(/\s+/g, " ").trim();
    }
    // Insert a space at block boundaries (`</p>`, `<br>`, `</li>`, …) so adjacent
    // blocks' text doesn't run together (e.g. "first.Second") once tags are gone.
    const spaced = value
      .replace(/<\/(p|div|li|h[1-6]|blockquote|tr)>/gi, "$& ")
      .replace(/<br\s*\/?>/gi, " ");
    if (typeof DOMParser === "undefined") {
      return decodeCommonEntities(
        spaced
          .replace(/<!--[\s\S]*?-->/g, " ")
          .replace(/<(?:\/?[a-zA-Z]|![a-zA-Z])[^>]*>/g, " ")
          .replace(REGEX_NBSP, " "),
      )
        .replace(/\s+/g, " ")
        .trim();
    }
    const doc = new DOMParser!().parseFromString(spaced, "text/html");
    return (doc.body.textContent ?? "").replace(/\s+/g, " ").trim();
  });
}

/**
 * Like `richTextToPlainText`, but keeps paragraph/line structure: block
 * boundaries become newlines instead of spaces. Use when the text leaves the
 * app (e.g. copying a note to the clipboard), where collapsing a
 * multi-paragraph note onto one line would mangle it.
 */
export function richTextToMultilineText(
  value: string | null | undefined,
): string {
  if (!value) return "";
  return memoize(multilineTextCache, value, () => {
    if (!isHtmlContent(value)) return tidyLines(stripMarkdownSyntax(value));
    const withBreaks = value
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/(p|div|li|h[1-6]|blockquote|tr)>/gi, "$&\n");
    const text =
      typeof DOMParser === "undefined"
        ? decodeCommonEntities(
            withBreaks
              .replace(/<!--[\s\S]*?-->/g, "")
              .replace(/<(?:\/?[a-zA-Z]|![a-zA-Z])[^>]*>/g, "")
              .replace(REGEX_NBSP, " "),
          )
        : (new DOMParser!().parseFromString(withBreaks, "text/html").body
            .textContent ?? "");
    return tidyLines(text);
  });
}
